import { useMemo, useRef, useState } from "react";
import type { UserRow } from "../sql";
import { fmtN, fmtPct } from "../../../lib/funnel/helpers";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

type NodeId =
  | "signup"
  | "transcript"
  | "summary"
  | "calendar"
  | "ask_meeting"
  | "ask_client"
  | "custom_template"
  | "plus"
  | "pro";

type NodeDef = {
  id: NodeId;
  label: string;
  terminal?: boolean;
  reached: (u: UserRow) => boolean;
  at: (u: UserRow) => string | null;
};

const NODES: NodeDef[] = [
  { id: "signup", label: "Sign up", reached: () => true, at: (u) => u.created_at_utc },
  { id: "transcript", label: "Got transcript", reached: (u) => u.transcript_count > 0, at: (u) => u.first_transcript_date },
  { id: "summary", label: "Got summary", reached: (u) => u.synopsis_count > 0, at: (u) => u.first_synopsis_date },
  { id: "calendar", label: "Connected calendar", reached: (u) => u.calendar_connected, at: (u) => u.first_calendar_date },
  { id: "ask_meeting", label: "Asked Marloo (meeting)", reached: (u) => u.chat_meeting_count > 0, at: (u) => u.first_chat_meeting_date },
  { id: "ask_client", label: "Asked Marloo (client)", reached: (u) => u.chat_client_count > 0, at: (u) => u.first_chat_client_date },
  { id: "custom_template", label: "Custom template", reached: (u) => u.custom_template_count > 0, at: (u) => u.first_custom_template_date },
  { id: "plus", label: "Plus plan", terminal: true, reached: (u) => u.on_paid_plan && u.billing_plan === "plus", at: (u) => u.subscription_started_date_utc },
  { id: "pro", label: "Pro plan", terminal: true, reached: (u) => u.on_paid_plan && u.billing_plan === "pro", at: (u) => u.subscription_started_date_utc },
];

const VIEW_W = 1040;
const VIEW_H = 540;
const NODE_R = 44;
const STORAGE_KEY = "signup-funnel-path-graph-layout-v1";
const DEFAULT_EDGE_MIN_PCT = 2;
const EDGE_THRESHOLD_MAX = 30;

const INITIAL_POSITIONS: Record<NodeId, [number, number]> = {
  signup: [80, 270],
  transcript: [260, 130],
  summary: [460, 70],
  calendar: [260, 410],
  ask_meeting: [580, 180],
  ask_client: [580, 360],
  custom_template: [760, 470],
  plus: [940, 180],
  pro: [940, 360],
};

function loadPositions(): Record<NodeId, [number, number]> {
  if (typeof window === "undefined") return INITIAL_POSITIONS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_POSITIONS;
    const parsed = JSON.parse(raw) as Partial<Record<NodeId, [number, number]>>;
    const merged = { ...INITIAL_POSITIONS };
    for (const k of Object.keys(merged) as NodeId[]) {
      const v = parsed[k];
      if (Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === "number")) {
        merged[k] = [v[0], v[1]];
      }
    }
    return merged;
  } catch {
    return INITIAL_POSITIONS;
  }
}

type Edge = { from: NodeId; to: NodeId; count: number; pct: number };

function buildEdges(users: UserRow[]): { edges: Edge[]; reachPct: Record<NodeId, number>; total: number } {
  const total = users.length;
  const reachCount = Object.fromEntries(NODES.map((n) => [n.id, 0])) as Record<NodeId, number>;
  const counts = new Map<string, number>();

  for (const u of users) {
    const ts: { id: NodeId; t: number }[] = [];
    for (const n of NODES) {
      if (!n.reached(u)) continue;
      reachCount[n.id] += 1;
      const raw = n.at(u);
      const t = raw ? Date.parse(raw) : NaN;
      ts.push({ id: n.id, t: Number.isNaN(t) ? Number.POSITIVE_INFINITY : t });
    }
    ts.sort((a, b) => a.t - b.t || NODES.findIndex((n) => n.id === a.id) - NODES.findIndex((n) => n.id === b.id));
    for (let i = 0; i < ts.length - 1; i++) {
      const a = ts[i];
      const b = ts[i + 1];
      if (!a || !b || a.id === b.id) continue;
      const k = `${a.id}->${b.id}`;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
  }

  const edges: Edge[] = [];
  for (const [k, count] of counts) {
    const [from, to] = k.split("->") as [NodeId, NodeId];
    const pct = total === 0 ? 0 : (count / total) * 100;
    edges.push({ from, to, count, pct });
  }
  edges.sort((a, b) => a.pct - b.pct);

  const reachPct = Object.fromEntries(
    (Object.keys(reachCount) as NodeId[]).map((k) => [k, total === 0 ? 0 : (reachCount[k] / total) * 100]),
  ) as Record<NodeId, number>;

  return { edges, reachPct, total };
}

const EDGE_COLOR = "var(--color-teal)";
const TERMINAL_COLOR = "var(--color-teal)";
const STEP_FILL = "var(--bg-1)";
const STEP_BORDER = "var(--border-strong)";

function curveMid(p0: [number, number], c1: [number, number], c2: [number, number], p3: [number, number]): [number, number] {
  const x = 0.125 * p0[0] + 0.375 * c1[0] + 0.375 * c2[0] + 0.125 * p3[0];
  const y = 0.125 * p0[1] + 0.375 * c1[1] + 0.375 * c2[1] + 0.125 * p3[1];
  return [x, y];
}

export function OnboardingPathGraph({ users, pagePills }: { users: UserRow[]; pagePills?: FilterPillSpec[] }) {
  const { edges: allEdges, reachPct, total } = useMemo(() => buildEdges(users), [users]);
  const [minPctThreshold, setMinPctThreshold] = useState<number>(DEFAULT_EDGE_MIN_PCT);
  const edges = useMemo(() => allEdges.filter((e) => e.pct >= minPctThreshold), [allEdges, minPctThreshold]);
  const hiddenCount = allEdges.length - edges.length;
  const [positions, setPositions] = useState<Record<NodeId, [number, number]>>(() => loadPositions());
  const [hovered, setHovered] = useState<{ kind: "node" | "edge"; key: string } | null>(null);
  const dragRef = useRef<{ id: NodeId; offX: number; offY: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const persist = (next: Record<NodeId, [number, number]>) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable; in-memory state still works
    }
  };

  const toSvg = (clientX: number, clientY: number): [number, number] => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const ctm = svg.getScreenCTM();
    if (!ctm) return [0, 0];
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const m = ctm.inverse();
    const out = pt.matrixTransform(m);
    return [out.x, out.y];
  };

  const onNodePointerDown = (id: NodeId) => (e: React.PointerEvent<SVGGElement>) => {
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const [sx, sy] = toSvg(e.clientX, e.clientY);
    const [nx, ny] = positions[id];
    dragRef.current = { id, offX: sx - nx, offY: sy - ny };
    e.stopPropagation();
  };

  const onNodePointerMove = (e: React.PointerEvent<SVGGElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const [sx, sy] = toSvg(e.clientX, e.clientY);
    const nx = Math.max(NODE_R, Math.min(VIEW_W - NODE_R, sx - drag.offX));
    const ny = Math.max(NODE_R, Math.min(VIEW_H - NODE_R, sy - drag.offY));
    setPositions((prev) => ({ ...prev, [drag.id]: [nx, ny] }));
  };

  const onNodePointerUp = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setPositions((prev) => {
      persist(prev);
      return prev;
    });
  };

  const resetLayout = () => {
    setPositions(INITIAL_POSITIONS);
    persist(INITIAL_POSITIONS);
  };

  const maxPct = edges.reduce((m, e) => Math.max(m, e.pct), 0);
  const minPct = edges.reduce((m, e) => Math.min(m, e.pct), Number.POSITIVE_INFINITY);

  const dimEdge = (e: Edge) =>
    hovered?.kind === "node" && hovered.key !== e.from && hovered.key !== e.to;
  const dimNode = (id: NodeId) => {
    if (!hovered) return false;
    if (hovered.kind === "node") {
      if (hovered.key === id) return false;
      return !edges.some(
        (e) => (e.from === hovered.key && e.to === id) || (e.to === hovered.key && e.from === id),
      );
    }
    const edge = edges.find((e) => `${e.from}->${e.to}` === hovered.key);
    return edge ? edge.from !== id && edge.to !== id : false;
  };

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  if (minPctThreshold > 0) {
    pills.push({ kind: "mode", label: `Edges below ${minPctThreshold}% hidden` });
  }

  return (
    <section className="card" style={{ padding: 20 }}>
      <SectionCardHeader
        title="Onboarding paths"
        subtitle={`Most-walked routes from sign-up to paid conversion across ${fmtN(total)} users in the current filter. Edge thickness and opacity scale with the share of the cohort taking that transition. Drag any node to rearrange.`}
        filterPills={pills}
        headerExtra={
          <button
            type="button"
            onClick={resetLayout}
            style={{
              background: "transparent",
              border: "1px solid var(--border-default)",
              color: "var(--fg-2)",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Reset layout
          </button>
        }
      />

      <div style={{ width: "100%" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          style={{ width: "100%", height: "auto", display: "block", userSelect: "none" }}
          onPointerMove={onNodePointerMove}
          onPointerUp={onNodePointerUp}
          onPointerLeave={onNodePointerUp}
        >

          {edges.map((e) => {
            const [x1, y1] = positions[e.from];
            const [x2, y2] = positions[e.to];
            const dx = x2 - x1;
            const dy = y2 - y1;
            const len = Math.hypot(dx, dy) || 1;
            const ux = dx / len;
            const uy = dy / len;
            const px = -uy;
            const py = ux;
            const bow = Math.min(120, len * 0.22);
            const c1: [number, number] = [x1 + dx * 0.3 + px * bow, y1 + dy * 0.3 + py * bow];
            const c2: [number, number] = [x1 + dx * 0.7 + px * bow, y1 + dy * 0.7 + py * bow];

            const trim = NODE_R + 6;
            const endDx = x2 - c2[0];
            const endDy = y2 - c2[1];
            const endLen = Math.hypot(endDx, endDy) || 1;
            const ex = x2 - (endDx / endLen) * trim;
            const ey = y2 - (endDy / endLen) * trim;
            const startDx = c1[0] - x1;
            const startDy = c1[1] - y1;
            const startLen = Math.hypot(startDx, startDy) || 1;
            const sx = x1 + (startDx / startLen) * (NODE_R + 2);
            const sy = y1 + (startDy / startLen) * (NODE_R + 2);

            const norm = maxPct === 0 ? 0 : e.pct / maxPct;
            const t = Math.pow(norm, 0.6);
            const width = 1.25 + t * 4.5;
            const opacity = 0.15 + t * 0.85;
            const color = EDGE_COLOR;
            const dim = dimEdge(e);
            const key = `${e.from}->${e.to}`;
            const [lx, ly] = curveMid([sx, sy], c1, c2, [ex, ey]);
            const markerId = `arr-${e.from}-${e.to}`;

            return (
              <g
                key={key}
                opacity={dim ? 0.08 : 1}
                onPointerEnter={() => setHovered({ kind: "edge", key })}
                onPointerLeave={() => setHovered(null)}
                style={{ cursor: "default" }}
              >
                <defs>
                  <marker
                    id={markerId}
                    viewBox="0 0 10 10"
                    refX="8"
                    refY="5"
                    markerWidth="5"
                    markerHeight="5"
                    orient="auto-start-reverse"
                    markerUnits="strokeWidth"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
                  </marker>
                </defs>
                <path
                  d={`M ${sx} ${sy} C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${ex} ${ey}`}
                  fill="none"
                  stroke={color}
                  strokeWidth={width}
                  strokeOpacity={opacity}
                  strokeLinecap="round"
                  markerEnd={`url(#${markerId})`}
                />
                <path
                  d={`M ${sx} ${sy} C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${ex} ${ey}`}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={Math.max(width + 8, 14)}
                />
                <text
                  x={lx}
                  y={ly}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={color}
                  stroke="var(--bg-1)"
                  strokeWidth={3}
                  style={{ pointerEvents: "none", paintOrder: "stroke" }}
                >
                  {e.pct.toFixed(0)}%
                </text>
                <title>
                  {`${nodeLabel(e.from)} → ${nodeLabel(e.to)}: ${e.pct.toFixed(1)}% (${fmtN(e.count)} users)`}
                </title>
              </g>
            );
          })}

          {NODES.map((n) => {
            const [x, y] = positions[n.id];
            const dim = dimNode(n.id);
            const isTerminal = n.terminal;
            const labelLines = wrapLabel(n.label);
            const isEmpty = reachPct[n.id] === 0;
            return (
              <g
                key={n.id}
                transform={`translate(${x} ${y})`}
                opacity={dim ? 0.2 : isEmpty ? 0.4 : 1}
                onPointerDown={onNodePointerDown(n.id)}
                onPointerEnter={() => !dragRef.current && setHovered({ kind: "node", key: n.id })}
                onPointerLeave={() => !dragRef.current && setHovered(null)}
                style={{ cursor: dragRef.current?.id === n.id ? "grabbing" : "grab", touchAction: "none" }}
              >
                <circle
                  r={NODE_R}
                  fill={isTerminal ? TERMINAL_COLOR : STEP_FILL}
                  stroke={isTerminal ? "transparent" : STEP_BORDER}
                  strokeWidth={1}
                />
                <text
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={isTerminal ? "#fff" : "var(--fg-1)"}
                  y={labelLines.length === 1 ? -2 : -8}
                  style={{ pointerEvents: "none" }}
                >
                  {labelLines.map((line, i) => (
                    <tspan key={i} x={0} dy={i === 0 ? 0 : 13}>
                      {line}
                    </tspan>
                  ))}
                </text>
                <text
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={500}
                  fill={isTerminal ? "rgba(255,255,255,0.85)" : "var(--fg-3)"}
                  y={labelLines.length === 1 ? 14 : labelLines.length * 13 - 2}
                  style={{ pointerEvents: "none" }}
                >
                  {fmtPct(Math.round((reachPct[n.id] / 100) * total), total)}
                </text>
                <title>{`${n.label}: ${reachPct[n.id].toFixed(1)}% reached`}</title>
              </g>
            );
          })}
        </svg>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
          flexWrap: "wrap",
          fontSize: 12,
          color: "var(--fg-3)",
          marginTop: 16,
          paddingTop: 14,
          borderTop: "1px solid var(--border-default)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{minPct === Number.POSITIVE_INFINITY ? "0%" : `${minPct.toFixed(0)}%`}</span>
          <span
            style={{
              display: "inline-block",
              width: 120,
              height: 8,
              borderRadius: 4,
              background: "linear-gradient(90deg, color-mix(in srgb, var(--color-teal) 12%, transparent), var(--color-teal))",
            }}
          />
          <span>{maxPct === 0 ? "0%" : `${maxPct.toFixed(0)}%`}</span>
          <span style={{ marginLeft: 4 }}>Edge weight = traffic share</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: 7,
              background: "var(--bg-1)",
              border: "1px solid var(--border-strong)",
            }}
          />
          <span>Step</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: 7,
              background: "var(--color-teal)",
            }}
          />
          <span>Paid plan</span>
        </div>
        <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span>Hide edges below</span>
          <input
            type="range"
            min={0}
            max={EDGE_THRESHOLD_MAX}
            step={1}
            value={minPctThreshold}
            onChange={(e) => setMinPctThreshold(Number(e.target.value))}
            style={{ width: 120, accentColor: "var(--color-teal)" }}
          />
          <span style={{ minWidth: 28, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--fg-2)" }}>
            {minPctThreshold}%
          </span>
          <span style={{ color: "var(--fg-muted)" }}>
            ({hiddenCount} hidden)
          </span>
        </label>
      </div>
    </section>
  );
}

function nodeLabel(id: NodeId): string {
  return NODES.find((n) => n.id === id)?.label ?? id;
}

function wrapLabel(label: string): string[] {
  if (label.length <= 14) return [label];
  const words = label.split(" ");
  if (words.length === 1) return [label];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}
